import React, { useEffect, useState, useCallback } from 'react';
import { getPartenaires, getComptaResume, getComptaMensuel, getComptaParPartenaire, getComptaChargesParType, getComptaPaiementsParType } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

const COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#f44336', '#9C27B0', '#009688', '#795548', '#E91E63', '#3F51B5', '#607D8B'];

const fmt = (v: any) => {
  const n = Number(v) || 0;
  if (!n) return '0';
  if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1000) return Math.round(n / 1000).toLocaleString('fr-FR') + 'K';
  return n.toLocaleString('fr-FR');
};

const Comptabilite: React.FC = () => {
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [filtrePartenaire, setFiltrePartenaire] = useState('');
  const [filtreAnnee, setFiltreAnnee] = useState(String(new Date().getFullYear()));
  const [filtreMois, setFiltreMois] = useState('');

  const [resume, setResume] = useState<any>(null);
  const [mensuel, setMensuel] = useState<any[]>([]);
  const [parPartenaire, setParPartenaire] = useState<any[]>([]);
  const [chargesParType, setChargesParType] = useState<any[]>([]);
  const [paiementsParType, setPaiementsParType] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPartenaires().then(r => setPartenaires(r.data));
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const p: Record<string, string> = {};
    if (filtrePartenaire) p.partenaire_id = filtrePartenaire;
    if (filtreAnnee) p.annee = filtreAnnee;
    if (filtreMois) p.mois = filtreMois;

    Promise.all([
      getComptaResume(p),
      getComptaMensuel({ ...p, mois: undefined } as any),
      getComptaParPartenaire(p),
      getComptaChargesParType(p),
      getComptaPaiementsParType(p),
    ]).then(([res, mens, part, chg, pay]) => {
      setResume(res.data);
      setMensuel(mens.data);
      setParPartenaire(part.data);
      setChargesParType(chg.data);
      setPaiementsParType(pay.data);
      setLoading(false);
    });
  }, [filtrePartenaire, filtreAnnee, filtreMois]);

  useEffect(() => { loadData(); }, [loadData]);

  const moisNoms = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
  const annees = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) annees.push(y);

  const r = resume;
  const margeColor = r?.resultat?.margeNette >= 0 ? '#4CAF50' : '#f44336';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#333', margin: 0 }}>Comptabilite</h2>
        <button
          onClick={loadData}
          disabled={loading}
          style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: loading ? '#bdbdbd' : '#1976d2', color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <span style={{ display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : 'none' }}>&#8635;</span>
          {loading ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
          <select value={filtrePartenaire} onChange={e => setFiltrePartenaire(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            <option value="">Tous les partenaires</option>
            {partenaires.map((p: any) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
          </select>
          <select value={filtreAnnee} onChange={e => setFiltreAnnee(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            {annees.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filtreMois} onChange={e => setFiltreMois(e.target.value)} className="filter-select" style={{ fontSize: 13 }}>
            <option value="">Tous les mois</option>
            {moisNoms.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          {loading && <span style={{ color: '#888', fontSize: 12 }}>Chargement...</span>}
        </div>
      </div>

      {r && (
        <>
          {/* KPIs Row 1: Facturation */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
            <KpiCard label="Total facture" value={fmt(r.facturation.totalFacture) + ' F'} sub={`${r.facturation.nbFactures.toLocaleString('fr-FR')} factures`} color="#2196F3" />
            <KpiCard label="Total recouvre" value={fmt(r.facturation.totalPaye) + ' F'} sub={`Taux: ${r.facturation.tauxRecouvrement}%`} color="#4CAF50" />
            <KpiCard label="Reste a recouvrer" value={fmt(r.facturation.resteARecouvrer) + ' F'} sub={`${r.facturation.nbImpayes} factures impayees`} color="#f44336" />
            <KpiCard label="Total charges" value={fmt(r.charges.totalCharges) + ' F'} sub={`${r.charges.nbCharges} operations`} color="#FF9800" />
          </div>

          {/* KPIs Row 2: Resultat */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
            <div className="finance-card">
              <div className="finance-icon" style={{ background: '#e8f5e9', color: '#2e7d32' }}>&#128176;</div>
              <div>
                <div className="finance-label">Recettes</div>
                <div className="finance-value" style={{ color: '#2e7d32' }}>{fmt(r.resultat.recettes)} F</div>
              </div>
            </div>
            <div className="finance-card">
              <div className="finance-icon" style={{ background: '#fff3e0', color: '#e65100' }}>&#128181;</div>
              <div>
                <div className="finance-label">Depenses</div>
                <div className="finance-value" style={{ color: '#e65100' }}>{fmt(r.resultat.depenses)} F</div>
              </div>
            </div>
            <div className="finance-card">
              <div className="finance-icon" style={{ background: r.resultat.margeNette >= 0 ? '#e8f5e9' : '#ffebee', color: margeColor }}>
                {r.resultat.margeNette >= 0 ? '▲' : '▼'}
              </div>
              <div>
                <div className="finance-label">Marge nette</div>
                <div className="finance-value" style={{ color: margeColor }}>{fmt(r.resultat.margeNette)} F</div>
              </div>
            </div>
            <div className="finance-card">
              <div className="finance-icon" style={{ background: '#e3f2fd', color: '#1565C0' }}>%</div>
              <div>
                <div className="finance-label">Marge %</div>
                <div className="finance-value" style={{ color: r.resultat.margePercent >= 0 ? '#2e7d32' : '#c62828' }}>{r.resultat.margePercent}%</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Monthly chart */}
        <div className="chart-card">
          <h3>Evolution mensuelle ({filtreAnnee})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={mensuel} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" fontSize={11} tick={{ fill: '#666' }} />
              <YAxis fontSize={11} tick={{ fill: '#666' }} tickFormatter={(v: any) => fmt(Number(v))} />
              <Tooltip formatter={(v: any, name: any) => [Number(v).toLocaleString('fr-FR') + ' F', name === 'facture' ? 'Facture' : name === 'paye' ? 'Recouvre' : 'Charges']} />
              <Bar dataKey="facture" name="Facture" fill="#2196F3" radius={[3, 3, 0, 0]} />
              <Bar dataKey="paye" name="Recouvre" fill="#4CAF50" radius={[3, 3, 0, 0]} />
              <Bar dataKey="charges" name="Charges" fill="#FF9800" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Marge mensuelle line */}
        <div className="chart-card">
          <h3>Marge nette mensuelle ({filtreAnnee})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={mensuel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" fontSize={11} tick={{ fill: '#666' }} />
              <YAxis fontSize={11} tick={{ fill: '#666' }} tickFormatter={(v: any) => fmt(Number(v))} />
              <Tooltip formatter={(v: any) => [Number(v).toLocaleString('fr-FR') + ' F', 'Marge']} />
              <Line type="monotone" dataKey="marge" stroke="#4CAF50" strokeWidth={3} dot={{ fill: '#4CAF50', r: 4 }} />
              <Line type="monotone" dataKey="paye" stroke="#2196F3" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pie charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Charges par type */}
        <div className="chart-card">
          <h3>Repartition des charges par type</h3>
          {chargesParType.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie data={chargesParType} dataKey="total" nameKey="type" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                    {chargesParType.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [Number(v).toLocaleString('fr-FR') + ' F']} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, fontSize: 12 }}>
                {chargesParType.map((c: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <span style={{ flex: 1, color: '#444' }}>{c.type || 'Autre'}</span>
                    <span style={{ fontWeight: 600, color: '#333' }}>{fmt(c.total)} F</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <div style={{ color: '#aaa', textAlign: 'center', padding: 40 }}>Aucune charge</div>}
        </div>

        {/* Paiements par type */}
        <div className="chart-card">
          <h3>Modes de paiement</h3>
          {paiementsParType.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie data={paiementsParType} dataKey="total" nameKey="type" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                    {paiementsParType.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [Number(v).toLocaleString('fr-FR') + ' F']} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1, fontSize: 12 }}>
                {paiementsParType.map((p: any, i: number) => {
                  const totalPay = paiementsParType.reduce((s: number, x: any) => s + x.total, 0);
                  const pct = totalPay > 0 ? Math.round((p.total / totalPay) * 100) : 0;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                      <span style={{ flex: 1, color: '#444' }}>{p.type}</span>
                      <span style={{ fontWeight: 600, color: '#333' }}>{fmt(p.total)} F</span>
                      <span style={{ fontSize: 10, color: '#888' }}>({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : <div style={{ color: '#aaa', textAlign: 'center', padding: 40 }}>Aucun paiement</div>}
        </div>
      </div>

      {/* Table par partenaire */}
      <div className="data-table-wrapper">
        <h3 style={{ marginBottom: 15, color: '#333' }}>Bilan par partenaire</h3>
        <table className="data-table" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Partenaire</th>
              <th style={{ textAlign: 'center' }}>Factures</th>
              <th style={{ textAlign: 'right' }}>Total facture</th>
              <th style={{ textAlign: 'right' }}>Recouvre</th>
              <th style={{ textAlign: 'right' }}>Reste</th>
              <th style={{ textAlign: 'center' }}>Taux</th>
              <th style={{ textAlign: 'right' }}>Charges</th>
              <th style={{ textAlign: 'right' }}>Marge nette</th>
            </tr>
          </thead>
          <tbody>
            {parPartenaire.map((p: any) => (
              <tr key={p.id}>
                <td><strong>{p.partenaire}</strong></td>
                <td style={{ textAlign: 'center' }}>{Number(p.nbFactures || 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right' }}>{Number(p.totalFacture || 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>{Number(p.totalPaye || 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right', color: Number(p.resteARecouvrer) > 0 ? '#c62828' : '#2e7d32' }}>{Number(p.resteARecouvrer || 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    background: p.tauxRecouvrement >= 50 ? '#e8f5e9' : p.tauxRecouvrement >= 25 ? '#fff3e0' : '#ffebee',
                    color: p.tauxRecouvrement >= 50 ? '#2e7d32' : p.tauxRecouvrement >= 25 ? '#e65100' : '#c62828'
                  }}>{p.tauxRecouvrement}%</span>
                </td>
                <td style={{ textAlign: 'right', color: '#e65100' }}>{Number(p.totalCharges || 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: Number(p.margeNette) >= 0 ? '#2e7d32' : '#c62828' }}>
                  {Number(p.margeNette || 0).toLocaleString('fr-FR')}
                </td>
              </tr>
            ))}
            {/* Total row */}
            {parPartenaire.length > 0 && (
              <tr style={{ background: '#f5f5f5', fontWeight: 700 }}>
                <td>TOTAL</td>
                <td style={{ textAlign: 'center' }}>{parPartenaire.reduce((s: number, p: any) => s + Number(p.nbFactures || 0), 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right' }}>{parPartenaire.reduce((s: number, p: any) => s + Number(p.totalFacture || 0), 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right', color: '#2e7d32' }}>{parPartenaire.reduce((s: number, p: any) => s + Number(p.totalPaye || 0), 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right', color: '#c62828' }}>{parPartenaire.reduce((s: number, p: any) => s + Number(p.resteARecouvrer || 0), 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'center' }}>-</td>
                <td style={{ textAlign: 'right', color: '#e65100' }}>{parPartenaire.reduce((s: number, p: any) => s + Number(p.totalCharges || 0), 0).toLocaleString('fr-FR')}</td>
                <td style={{ textAlign: 'right', color: parPartenaire.reduce((s: number, p: any) => s + Number(p.margeNette || 0), 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                  {parPartenaire.reduce((s: number, p: any) => s + Number(p.margeNette || 0), 0).toLocaleString('fr-FR')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; sub: string; color: string }> = ({ label, value, sub, color }) => (
  <div className="kpi-card-v2" style={{ borderLeft: `4px solid ${color}`, padding: '14px 16px' }}>
    <div className="kpi-v2-label" style={{ fontSize: 11 }}>{label}</div>
    <div className="kpi-v2-value" style={{ fontSize: 22, color }}>{value}</div>
    <div className="kpi-v2-sub">{sub}</div>
  </div>
);

export default Comptabilite;
